import { useState } from 'react'
import { Lock, Sparkles } from 'lucide-react'
import { motion } from 'framer-motion'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'

export function LockScreen() {
  const { user, unlock } = useAuth()
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!user) return null

  const initials = user.name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  async function handleSubmit(event) {
    event.preventDefault()
    setError('')
    setSubmitting(true)

    try {
      await unlock(password)
      setPassword('')
    } catch (err) {
      const message =
        err?.response?.data?.errors?.password?.[0] ??
        err?.response?.data?.message ??
        'Unable to unlock. Check your password.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="lock-screen-title"
    >
      <div className="absolute inset-0 bg-[hsl(262_20%_14%/0.55)] backdrop-blur-md" />

      <motion.div
        className="glass-luxury relative w-full max-w-md rounded-3xl p-8 shadow-2xl"
        initial={{ scale: 0.96, y: 12 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
      >
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-[hsl(262_70%_48%)] text-lg font-bold text-white shadow-md">
            {initials || 'LM'}
          </div>
          <div className="mb-1 flex items-center gap-1.5 text-primary">
            <Lock className="h-4 w-4" aria-hidden />
            <span className="text-[11px] font-semibold uppercase tracking-widest">Screen locked</span>
          </div>
          <h1 id="lock-screen-title" className="text-xl font-semibold tracking-tight">
            Welcome back, {user.name.split(' ')[0]}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter your password to continue your session.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="unlock-password" className="sr-only">
              Password
            </label>
            <input
              id="unlock-password"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring',
              )}
            />
          </div>

          {error ? (
            <p className="text-center text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <Button type="submit" className="w-full" disabled={submitting || !password}>
            {submitting ? 'Unlocking…' : 'Unlock'}
          </Button>
        </form>

        <p className="mt-6 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
          <Sparkles className="h-3 w-3 text-gold" aria-hidden />
          Lumière Ladies Spa · Secure session
        </p>
      </motion.div>
    </motion.div>
  )
}
